/**
 * 검색 입력의 초안 상태.
 *
 * 타이핑마다 조회를 보내면 250ms RTT 에서 요청이 겹친다(규칙 §6). 초안은 컴포넌트가 들고,
 * 커밋만 밖으로 낸다.
 *
 * Owns:
 * - 초안 값, 지연 커밋 타이머, 즉시 커밋 경로, 외부 값 변경의 초안 덮어쓰기.
 *
 * Does not own:
 * - 커밋된 값의 저장 위치(URL · 상태) — 호출부가 정한다.
 */
import { useCallback, useEffect, useRef, useState } from "react";

// 타이핑이 멈춘 뒤 조회까지의 지연. RTT(250ms) 한 번보다 조금 길게 둬서 한 단어를 치는 동안
// 요청이 쌓이지 않게 한다.
export const SEARCH_COMMIT_DELAY_MS = 300;

export type SearchDraft = {
  draft: string;
  /** 입력 변경 — 지연 커밋을 예약한다. 빈 값은 지우기라 즉시 커밋한다. */
  change: (next: string) => void;
  /** Enter · 검색 버튼 — 예약을 취소하고 지금 커밋한다. */
  commitNow: (next?: string) => void;
};

export function useSearchDraft(
  value: string,
  onCommit: (next: string) => void,
  delayMs: number = SEARCH_COMMIT_DELAY_MS,
): SearchDraft {
  const [draft, setDraft] = useState(value);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // 커밋 함수는 호출부가 매 렌더 새로 만들어도 타이머가 최신 것을 부르게 ref 로 든다.
  const commitRef = useRef(onCommit);
  commitRef.current = onCommit;
  // 외부 변경 판정용 — 우리가 방금 커밋한 값이 되돌아온 것과 뒤로 가기 같은 외부 변경을 가른다.
  const lastSeenValueRef = useRef(value);

  const clearTimer = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  useEffect(() => clearTimer, [clearTimer]);

  useEffect(() => {
    if (value === lastSeenValueRef.current) {
      return;
    }

    // 외부(URL 복원 · 초기화 버튼)가 값을 바꿨다 — 예약된 초안은 버린다.
    lastSeenValueRef.current = value;
    clearTimer();
    setDraft(value);
  }, [clearTimer, value]);

  const commit = useCallback(
    (next: string) => {
      clearTimer();
      lastSeenValueRef.current = next;
      commitRef.current(next);
    },
    [clearTimer],
  );

  const change = useCallback(
    (next: string) => {
      setDraft(next);
      clearTimer();

      // 지우기(✕ · 전부 지움)는 "조건 해제" 라 기다릴 이유가 없다.
      if (next === "") {
        commit("");
        return;
      }

      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        commit(next);
      }, delayMs);
    },
    [clearTimer, commit, delayMs],
  );

  const commitNow = useCallback(
    (next?: string) => {
      const target = next ?? draft;
      setDraft(target);
      commit(target);
    },
    [commit, draft],
  );

  return { draft, change, commitNow };
}
